import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { Loader2, ArrowRight, AlertTriangle, Check } from "lucide-react";

export default function AuditResult() {
  const { auditId } = useParams();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const pollRef = useRef(null);

  const fetchOnce = async () => {
    try {
      const res = await api.get(`/audit/${auditId}`);
      setAudit(res.data);
      setLoading(false);
      if (res.data.status === "ready" || res.data.status === "error") {
        if (pollRef.current) clearInterval(pollRef.current);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load audit");
      setLoading(false);
      if (pollRef.current) clearInterval(pollRef.current);
    }
  };

  useEffect(() => {
    fetchOnce();
    pollRef.current = setInterval(fetchOnce, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId]);

  return (
    <div className="grain min-h-screen flex flex-col">
      <Nav />
      <main className="flex-1 py-16 lg:py-24">
        <div className="max-w-[1080px] mx-auto px-6 lg:px-10">
          {loading && !audit && <Skeleton />}
          {err && <ErrorView msg={err} />}
          {audit && audit.status === "generating" && <Generating audit={audit} />}
          {audit && audit.status === "error" && <ErrorView msg={audit.error || "The audit failed to generate."} />}
          {audit && audit.status === "ready" && <Ready audit={audit} />}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Skeleton() {
  return (
    <div className="card-paper rounded-2xl p-10 animate-pulse">
      <div className="h-6 w-32 bg-ink/10 rounded"></div>
      <div className="h-12 w-3/4 bg-ink/10 rounded mt-6"></div>
      <div className="h-4 w-2/3 bg-ink/10 rounded mt-4"></div>
    </div>
  );
}

function Generating({ audit }) {
  return (
    <div className="card-paper rounded-2xl p-10 lg:p-14 text-center max-w-[720px] mx-auto" data-testid="audit-generating">
      <Loader2 size={36} className="text-rust animate-spin mx-auto" />
      <div className="mono text-xs uppercase tracking-[0.2em] text-mute mt-8">Generating audit</div>
      <h2 className="display text-4xl lg:text-5xl mt-4">
        Reading <span className="display-italic text-rust">{audit.business_name}</span>…
      </h2>
      <p className="text-ink/70 mt-4 max-w-[44ch] mx-auto">
        Our AI is scanning the typical leaks for {audit.industry || "service businesses"} like yours and crunching the dollar impact. Usually takes 20–40 seconds.
      </p>
      <div className="mt-8 mono text-[11px] uppercase tracking-wider text-mute flex items-center justify-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-rust pulse-dot"></span> Powered by Claude Sonnet 4.5
      </div>
    </div>
  );
}

function ErrorView({ msg }) {
  return (
    <div className="card-paper rounded-2xl p-10 max-w-[640px] mx-auto text-center" data-testid="audit-error">
      <AlertTriangle size={32} className="text-rust mx-auto" />
      <h2 className="display text-3xl mt-4">Audit hit a snag.</h2>
      <p className="text-ink/70 mt-3">{msg}</p>
      <Link to="/audit" className="btn-primary mt-6 inline-flex px-6 py-3 rounded-full text-sm">Try again →</Link>
    </div>
  );
}

function fmtMoney(n) {
  if (!n) return "$0";
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `$${n}`;
}

function Ready({ audit }) {
  return (
    <div className="space-y-10" data-testid="audit-ready">
      {/* HERO STRIP */}
      <div className="card-ink rounded-2xl p-8 lg:p-12 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-[500px] h-[500px] rounded-full bg-rust/15 blur-3xl"></div>
        <div className="relative">
          <div className="mono text-xs uppercase tracking-[0.2em] text-ember">Your audit · {audit.business_name}</div>
          <h1 className="display text-4xl lg:text-6xl mt-6 max-w-[22ch] text-cream leading-[1.02]" data-testid="audit-headline">
            {audit.headline}
          </h1>
          <p className="text-cream/75 mt-6 max-w-[58ch] text-base lg:text-lg leading-relaxed">{audit.summary}</p>

          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            <div className="border border-cream/15 rounded-xl p-5">
              <div className="mono text-[10px] uppercase tracking-wider text-cream/60">Conservative</div>
              <div className="display text-3xl mt-2 text-cream">{fmtMoney(audit.monthly_leak_low)}/mo</div>
              <div className="text-xs text-cream/60 mt-1">monthly revenue leak (low)</div>
            </div>
            <div className="border border-ember/40 rounded-xl p-5 bg-rust/10">
              <div className="mono text-[10px] uppercase tracking-wider text-ember">Realistic upper</div>
              <div className="display text-3xl mt-2 text-ember">{fmtMoney(audit.monthly_leak_high)}/mo</div>
              <div className="text-xs text-cream/60 mt-1">monthly revenue leak (high)</div>
            </div>
            <div className="border border-cream/15 rounded-xl p-5">
              <div className="mono text-[10px] uppercase tracking-wider text-cream/60">Annualized</div>
              <div className="display text-3xl mt-2 text-cream">
                {fmtMoney(((audit.monthly_leak_low || 0) + (audit.monthly_leak_high || 0)) / 2 * 12)}/yr
              </div>
              <div className="text-xs text-cream/60 mt-1">midpoint × 12</div>
            </div>
          </div>
        </div>
      </div>

      {/* SECTIONS */}
      <div className="grid lg:grid-cols-2 gap-5">
        {(audit.sections || []).map((s, i) => (
          <div key={i} className="card-paper rounded-2xl p-7" data-testid={`audit-section-${i}`}>
            <div className="mono text-[10px] uppercase tracking-wider text-rust">Leak {String(i + 1).padStart(2, "0")}</div>
            <h3 className="display text-2xl mt-3">{s.title}</h3>
            <p className="text-ink/75 mt-3 leading-relaxed text-[15px]">{s.body}</p>
          </div>
        ))}
      </div>

      {/* RECOMMENDATIONS */}
      <div className="card-paper rounded-2xl p-8 lg:p-10">
        <span className="tag mb-3">What we'd fix first</span>
        <h3 className="display text-3xl lg:text-4xl mt-4 max-w-[24ch]">
          The <span className="display-italic text-rust">5 moves</span> that recover the most money.
        </h3>
        <ul className="mt-8 grid md:grid-cols-2 gap-4">
          {(audit.recommendations || []).map((r, i) => (
            <li key={i} className="flex items-start gap-3 p-4 border border-ink/10 rounded-xl bg-cream/40" data-testid={`audit-rec-${i}`}>
              <span className="w-7 h-7 rounded-full bg-rust text-cream flex items-center justify-center mono text-xs shrink-0 mt-0.5">{i + 1}</span>
              <span className="leading-relaxed">{r}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA */}
      <div className="card-ink rounded-2xl p-10 lg:p-14 relative overflow-hidden">
        <div className="absolute -left-20 -bottom-20 w-[400px] h-[400px] rounded-full bg-rust/20 blur-3xl"></div>
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <span className="tag tag-dark">Ready to plug the leak?</span>
            <h3 className="display text-4xl lg:text-5xl mt-5 text-cream max-w-[20ch]">
              Let's build it. <span className="display-italic text-ember">Live in 7 days.</span>
            </h3>
            <p className="text-cream/70 mt-4 max-w-[48ch]">
              We'll get on a 15-min call, walk through the audit live, and tell you exactly what we'd build for {audit.business_name} and what it costs. No slides, no pitch.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3">
            <a href="https://calendly.com/roger-aiautomatesystems/30min" className="btn-cream px-6 py-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2" data-testid="audit-book-call">
              Book a 15-min strategy call <ArrowRight size={14} />
            </a>
            <Link to="/" className="border border-cream/30 px-6 py-4 rounded-full text-sm font-medium hover:bg-cream/10 transition text-center text-cream">
              Back to home
            </Link>
            <div className="mt-3 mono text-[11px] uppercase tracking-wider text-cream/60 flex items-center gap-2 justify-center">
              <Check size={12} /> 30-day ROI guarantee
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
